import {computed, effect, inject, Signal, untracked} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {CanActivateChildFn, NavigationEnd, Router, UrlTree} from '@angular/router';
import {filter, map} from 'rxjs';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {HOME_URL, SETTINGS_ACCOUNT_URL, SETTINGS_EXPORT_URL, SETTINGS_TENANT_URL, SETTINGS_URL} from 'src/app/models/home/navigation.model';

/**
 * What a suspended workspace keeps (KAB-2399): its blocking page, served on the home URL, the personal
 * settings, the workspace settings, and the data exports — its data stays its own. Which pages, not who: that
 * stays with each route's own guards — the workspace settings rely on their permissionGuard (TENANT_EDIT,
 * pinned by settings.component.spec.ts). The settings menu shows exactly these sections.
 */
const OPEN_WHILE_SUSPENDED: readonly string[] = [HOME_URL, SETTINGS_URL, SETTINGS_ACCOUNT_URL, SETTINGS_TENANT_URL, SETTINGS_EXPORT_URL];

export function isOpenWhileSuspended(url: string): boolean {
	return OPEN_WHILE_SUSPENDED.includes(url.split(/[?#]/)[0]);
}

/**
 * Sends every other page of a suspended workspace to its blocking page. A tenant not loaded yet passes on
 * purpose: HomeComponent, which this guard's route renders, is what loads it — waiting for it here would
 * block the first navigation for good. redirectWhenSuspended() takes over once it has loaded.
 */
export const suspendedTenantGuard: CanActivateChildFn = (_route, state) => {
	const tenant = inject(CurrentTenantStore);
	return !tenant.isSuspended() || isOpenWhileSuspended(state.url) || blockingPage(inject(Router), tenant);
};

/**
 * Guards only run on navigation, and the tenant loads after the first one: a workspace that turns out
 * suspended once loaded (deep link, restored tab, a switch — each is a page load) is sent to its blocking
 * page from here. Call from the logged-in shell's injection context.
 * Returns whether the current page may render: the shell holds back the page this is sending away, which
 * would only fetch what the engine refuses a suspended workspace (403).
 */
export function redirectWhenSuspended(): Signal<boolean> {
	const router = inject(Router);
	const tenant = inject(CurrentTenantStore);
	const url = toSignal(router.events.pipe(
		filter(event => event instanceof NavigationEnd),
		map(event => event.urlAfterRedirects),
	), {initialValue: router.url});
	const pageOpen = computed(() => !tenant.isSuspended() || isOpenWhileSuspended(url()));
	effect(() => {
		if (!pageOpen()) {
			untracked(() => router.navigateByUrl(blockingPage(router, tenant)));
		}
	});
	return pageOpen;
}

function blockingPage(router: Router, tenant: CurrentTenantStore): UrlTree {
	return router.createUrlTree([HOME_URL], {queryParams: {tenant: tenant.currentTenantId()}});
}
